import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import {
  UploadFile,
  UploadFiles,
  UploadImages,
  DeleteImages,
  RemoveFile,
  END_POINT,
  getJwtToken,
} from '../../Redux/AdminReducer/action';
import CustomSpinner from '../CommonComponents/CustomSpinner';
import {
  FileUploadModal,
  ImageShowModal,
  ImageUploadModal,
  MultipleFileUploads,
} from '../CommonComponents/DoctorUploadModals';
import AvailabilityCalendarAndTimeSlots from '../CommonComponents/CustomDoctorAvailibiityDateCalenderSlot';
import { Button, Input, Modal } from '../UI';
import { FiArrowLeft, FiSave, FiLogOut, FiUpload, FiEye, FiTrash2 } from 'react-icons/fi';
import { consultantTypes } from '../../Files/dropdownOptions';
import { useGetLoggedInUser } from '../../UtilityHookFunctions/LoggedInUser';

const initialFormData = {
  name: '',
  typesOfDoctor: '',
  email: '',
  phone: '',
  education: '',
  experience: '',
  fees: '',
  education_details: '',
  experience_details: '',
  password: '',
  confirmPassword: '',
};

const PASSWORD_MIN_LENGTH = 6;

// Roles the server lets change the website ("Meet Our Team") fields; see
// backend/utils/websiteTeam.js.
const WEBSITE_EDITOR_ROLES = ['MainDoctor', 'admin', 'Admin', 'MasterAdmin', 'SuperAdmin'];
// Receptionists are not listed on the website.
const WEBSITE_LISTED_ROLES = ['doctor', 'assistantDoctor', 'consultant', 'mainDoctor'];

// Routes answer with either a plain string or a { error } body; toast needs a string.
const errorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.msg === 'string') return data.msg;
  return fallback;
};

/**
 * Universal Staff Form Component
 * Consolidates DoctorForm, AssistantDoctorForm, ReceptionistForm, and ConsultantForm
 */
export default function StaffForm({
  role = 'doctor', // 'doctor' | 'assistantDoctor' | 'receptionist' | 'consultant' | 'mainDoctor'
  title = 'Doctor',
  returnUrl = '/doctors',
}) {
  const [formData, setFormData] = useState(null);
  const [formError, setFormError] = useState({});
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const navigate = useNavigate();
  const params = useParams();
  const isMainDoctor = role === 'mainDoctor';
  const staffId =
    params.Doctor_Id ||
    params.id ||
    params.assistantDoctor_id ||
    params.receptionistId ||
    params.consultantId ||
    Object.values(params)[0] ||
    (isMainDoctor ? 'profile' : 'addNew');

  const isAddNew = !isMainDoctor && staffId === 'addNew';
  // Consultants store typesOfDoctor as a fixed enum on the server; every other role treats it as free text.
  const isConsultant = role === 'consultant';
  const loggedInUser = useGetLoggedInUser();
  const canEditWebsite =
    WEBSITE_LISTED_ROLES.includes(role) && WEBSITE_EDITOR_ROLES.includes(loggedInUser?.role);

  useEffect(() => {
    if (isAddNew) {
      setFormData(initialFormData);
    } else {
      const fetchStaff = async () => {
        try {
          const fetchUrl = isMainDoctor
            ? (staffId && staffId !== 'addNew' && staffId !== 'profile'
                ? `${END_POINT}/mainDoctor/${staffId}`
                : `${END_POINT}/mainDoctor/profile`)
            : `${END_POINT}/${role}/${staffId}`;

          const res = await axios.get(fetchUrl, {
            headers: { Authorization: getJwtToken() },
          });
          const staff = res?.data || initialFormData;
          // Older consultant records hold free-text designations the enum no longer
          // accepts; blank those so the dropdown forces a valid choice before saving.
          if (role === 'consultant' && !consultantTypes.includes(staff?.typesOfDoctor)) {
            staff.typesOfDoctor = '';
          }
          setFormData(staff);
        } catch (error) {
          toast.error(errorMessage(error, `Failed to fetch ${title} details`));
        }
      };
      fetchStaff();
    }
  }, [staffId, role, isAddNew, isMainDoctor, title]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formError[name]) {
      setFormError((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData?.name?.trim()) errors.name = 'Full name is required';
    if (!formData?.email?.trim()) {
      errors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Invalid email address format';
    }
    if (!formData?.phone) {
      errors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(String(formData.phone).trim())) {
      errors.phone = 'Phone must be exactly 10 digits';
    }
    if (isConsultant && !formData?.typesOfDoctor) {
      errors.typesOfDoctor = 'Type of consultant is required';
    }

    // The login password is only set when the profile is first created.
    if (isAddNew) {
      if (!formData?.password) {
        errors.password = 'Login password is required';
      } else if (formData.password.trim().length < PASSWORD_MIN_LENGTH) {
        errors.password = `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
      }
      if (formData?.password !== formData?.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error('Please fix validation errors');
      return;
    }

    setIsProcessing(true);
    try {
      if (isAddNew) {
        const { confirmPassword, ...payload } = formData;
        await axios.post(
          `${END_POINT}/${role}`,
          { ...payload, password: payload.password?.trim() },
          { headers: { Authorization: getJwtToken() } }
        );
        toast.success(`${title} created successfully!`);
        navigate(returnUrl);
      } else {
        // Passwords are changed through the Reset Password screen, never here.
        const { password, confirmPassword, ...payload } = formData;
        const updateUrl = isMainDoctor
          ? (formData?._id ? `${END_POINT}/mainDoctor/profile/${formData._id}` : `${END_POINT}/mainDoctor/profile`)
          : `${END_POINT}/${role}/profile/${formData?._id}`;

        await axios.put(updateUrl, payload, {
          headers: { Authorization: getJwtToken() },
        });
        toast.success(`${title} details updated!`);
      }
    } catch (error) {
      toast.error(errorMessage(error, `Failed to save ${title}`));
    } finally {
      setIsProcessing(false);
    }
  };


  const handleExitConfirm = async () => {
    const updated = { ...formData, status: 'Left' };
    setFormData(updated);
    setIsExitModalOpen(false);
    setIsProcessing(true);
    try {
      await axios.put(`${END_POINT}/${role}/profile/${formData?._id}`, updated, {
        headers: { Authorization: getJwtToken() },
      });
      toast.success(`${title} marked as exited`);
    } catch (error) {
      toast.error('Failed to update exit status');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!formData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <CustomSpinner />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 w-full space-y-6">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            icon={<FiArrowLeft className="w-4 h-4" />}
          >
            Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">
              {isAddNew ? `Add New ${title}` : `Edit ${title} Profile`}
            </h1>
            <p className="text-xs text-slate-500">
              Manage personal credentials, documents, and scheduling
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isAddNew && !isMainDoctor && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExitModalOpen(true)}
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              icon={<FiLogOut className="w-4 h-4" />}
            >
              Mark as Left
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            isLoading={isProcessing}
            icon={<FiSave className="w-4 h-4" />}
          >
            {isAddNew ? 'Create Profile' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Personal Information Card */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm space-y-5">
        <h2 className="text-base font-bold text-slate-800 border-l-4 border-sky-500 pl-3">
          Personal & Professional Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            name="name"
            placeholder="e.g. Dr. Jane Doe"
            value={formData?.name || ''}
            onChange={handleInputChange}
            error={formError?.name}
            required
          />
          {isConsultant ? (
            <div className="w-full">
              <label
                htmlFor="typesOfDoctor"
                className="block mb-1.5 text-xs font-semibold text-slate-700 tracking-wide"
              >
                Type of Consultant
                <span className="text-rose-500 ml-1">*</span>
              </label>
              <select
                id="typesOfDoctor"
                name="typesOfDoctor"
                value={formData?.typesOfDoctor || ''}
                onChange={handleInputChange}
                className={`w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-slate-800 capitalize shadow-sm transition-all duration-150 outline-none ${
                  formError?.typesOfDoctor
                    ? 'border-rose-400 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20'
                    : 'border-slate-200 hover:border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20'
                }`}
              >
                <option value="">Select type</option>
                {consultantTypes.map((type) => (
                  <option key={type} value={type} className="capitalize">
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
              {formError?.typesOfDoctor && (
                <p className="mt-1.5 text-xs text-rose-500 font-medium">
                  {formError.typesOfDoctor}
                </p>
              )}
            </div>
          ) : (
            <Input
              label="Designation / Title"
              name="typesOfDoctor"
              placeholder="e.g. Senior Homeopath / Consultant"
              value={formData?.typesOfDoctor || ''}
              onChange={handleInputChange}
            />
          )}
          <Input
            label="Email Address"
            name="email"
            type="email"
            placeholder="doctor@clinic.com"
            value={formData?.email || ''}
            onChange={handleInputChange}
            error={formError?.email}
            required
          />
          <Input
            label="Contact Number"
            name="phone"
            type="tel"
            placeholder="10-digit mobile number"
            value={formData?.phone || ''}
            onChange={handleInputChange}
            error={formError?.phone}
            required
          />
          <Input
            label="Degree / Qualification"
            name="education"
            placeholder="e.g. BHMS, MD"
            value={formData?.education || ''}
            onChange={handleInputChange}
          />
          <Input
            label="Experience (in months)"
            name="experience"
            type="number"
            placeholder="e.g. 36"
            value={formData?.experience || ''}
            onChange={handleInputChange}
          />
          <Input
            label="Consultation Fee (₹)"
            name="fees"
            type="number"
            placeholder="e.g. 500"
            value={formData?.fees || ''}
            onChange={handleInputChange}
          />
          <Input
            label="Education Details"
            name="education_details"
            placeholder="University / Institute details"
            value={formData?.education_details || ''}
            onChange={handleInputChange}
          />
          <div className="md:col-span-2">
            <Input
              label="Experience Details"
              name="experience_details"
              placeholder="Past clinic / hospital experience summary"
              value={formData?.experience_details || ''}
              onChange={handleInputChange}
            />
          </div>
        </div>
      </div>

      {/* Public website listing (main doctor only) */}
      {canEditWebsite && (
        <WebsiteTeamSection
          role={role}
          formData={formData}
          setFormData={setFormData}
          onChange={handleInputChange}
        />
      )}

      {/* Login Credentials (set once, at creation) */}
      {isAddNew && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm space-y-5">
          <div>
            <h2 className="text-base font-bold text-slate-800 border-l-4 border-sky-500 pl-3">
              Login Credentials
            </h2>
            <p className="text-xs text-slate-500 pl-4 mt-1">
              The {title.toLowerCase()} signs in with the email address above and this password.
              It can be changed later from Reset Password.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Password"
              name="password"
              type="password"
              placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
              value={formData?.password || ''}
              onChange={handleInputChange}
              error={formError?.password}
              autoComplete="new-password"
              required
            />
            <Input
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              placeholder="Re-enter the password"
              value={formData?.confirmPassword || ''}
              onChange={handleInputChange}
              error={formError?.confirmPassword}
              autoComplete="new-password"
              required
            />
          </div>
        </div>
      )}

      {/* Document Uploads Card (when existing record) */}
      {formData?._id && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm space-y-5">
          <h2 className="text-base font-bold text-slate-800 border-l-4 border-sky-500 pl-3">
            Credentials & Document Attachments
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DocumentUploadField
              label="Education Certificate"
              uploadType="educationDoc"
              role={role}
              formData={formData}
            />
            <DocumentUploadField
              label="Experience Letter"
              uploadType="experienceDoc"
              role={role}
              formData={formData}
            />
            <DocumentUploadField
              label="Medical Registration"
              uploadType="registrationDoc"
              role={role}
              formData={formData}
            />
            <DocumentUploadField
              label="Resume / Curriculum Vitae"
              uploadType="resumeDoc"
              role={role}
              formData={formData}
            />
            <DocumentUploadField
              label="Other Documents"
              uploadType="otherDocs"
              role={role}
              formData={formData}
            />
          </div>
        </div>
      )}

      {/* Availability Calendar & Slots */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm space-y-4">
        <h2 className="text-base font-bold text-slate-800 border-l-4 border-sky-500 pl-3">
          Weekly Availability & Slot Schedule
        </h2>
        <AvailabilityCalendarAndTimeSlots
          formData={formData}
          setFormData={setFormData}
        />
      </div>

      {/* Bottom Save Action */}
      <div className="flex justify-end pt-2">
        <Button
          variant="primary"
          size="md"
          onClick={handleSubmit}
          isLoading={isProcessing}
          icon={<FiSave className="w-4 h-4" />}
        >
          {isAddNew ? `Create ${title} Profile` : 'Save All Changes'}
        </Button>
      </div>

      {/* Exit Confirmation Modal */}
      <Modal
        isOpen={isExitModalOpen}
        onClose={() => setIsExitModalOpen(false)}
        title={`Mark ${title} as Exited`}
        subtitle="Record exit date and reason for clinic personnel records"
        footer={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExitModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleExitConfirm}
            >
              Confirm Exit
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Exit Date"
            name="exit_date"
            type="date"
            value={formData?.exit_date || ''}
            onChange={handleInputChange}
          />
          <Input
            label="Exit Reason"
            name="exit_reason"
            placeholder="e.g. Relocation, Contract Completed"
            value={formData?.exit_reason || ''}
            onChange={handleInputChange}
          />
        </div>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

/**
 * "Website (Meet Our Team)" card: whether and how this person appears on the
 * public website's team page. Saved with the rest of the form.
 */
function WebsiteTeamSection({ role, formData, setFormData, onChange }) {
  const dispatch = useDispatch();
  const [isUploading, setIsUploading] = useState(false);
  const photoId = Array.isArray(formData?.profile_image) ? formData.profile_image[0] : null;

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !formData?._id) return;
    setIsUploading(true);
    try {
      const res = await dispatch(UploadImages([file], formData._id, 'profile_image', role));
      if (!res) return;
      // The upload answers with plain text, so read the new image id back.
      const fetchUrl =
        role === 'mainDoctor'
          ? `${END_POINT}/mainDoctor/profile/${formData._id}`
          : `${END_POINT}/${role}/${formData._id}`;
      const fresh = await axios.get(fetchUrl, { headers: { Authorization: getJwtToken() } });
      setFormData((prev) => ({ ...prev, profile_image: fresh?.data?.profile_image || [] }));
      toast.success('Website photo uploaded');
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to upload photo'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm space-y-5">
      <div>
        <h2 className="text-base font-bold text-slate-800 border-l-4 border-sky-500 pl-3">
          Website (Meet Our Team)
        </h2>
        <p className="text-xs text-slate-500 pl-4 mt-1">
          Controls how this person appears on the clinic website&apos;s team page. Only the
          name, website role, qualification, bio and photo are published.
        </p>
      </div>

      <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 cursor-pointer w-fit">
        <input
          type="checkbox"
          name="showOnWebsite"
          checked={Boolean(formData?.showOnWebsite)}
          onChange={(e) => setFormData((prev) => ({ ...prev, showOnWebsite: e.target.checked }))}
          className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
        />
        Show on website
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Website Role"
          name="websiteRole"
          placeholder="e.g. Pediatric Homeopath"
          value={formData?.websiteRole || ''}
          onChange={onChange}
          helperText="Shown under the name. Defaults to the designation when empty."
        />
        <Input
          label="Display Order"
          name="websiteOrder"
          type="number"
          placeholder="e.g. 1"
          value={formData?.websiteOrder ?? ''}
          onChange={onChange}
          helperText="Lower numbers appear first; ties are sorted by name."
        />
        <div className="md:col-span-2">
          <label
            htmlFor="websiteBio"
            className="block mb-1.5 text-xs font-semibold text-slate-700 tracking-wide"
          >
            Website Bio
          </label>
          <textarea
            id="websiteBio"
            name="websiteBio"
            rows={3}
            placeholder="A short introduction for patients"
            value={formData?.websiteBio || ''}
            onChange={onChange}
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 shadow-sm transition-all duration-150 outline-none hover:border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
          />
        </div>
      </div>

      {formData?._id ? (
        <div className="flex items-center gap-3">
          {photoId ? (
            <img
              src={`${END_POINT}/${role}/get/image/${photoId}`}
              alt="Website"
              className="w-14 h-14 rounded-full object-cover border border-slate-200"
            />
          ) : (
            <span className="w-14 h-14 rounded-full bg-slate-100 border border-slate-200 text-[11px] text-slate-400 flex items-center justify-center">
              No photo
            </span>
          )}
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer">
            <FiUpload className="w-3 h-3" />
            {isUploading ? 'Uploading…' : photoId ? 'Replace Photo' : 'Upload Photo'}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={isUploading}
              onChange={handlePhotoUpload}
            />
          </label>
        </div>
      ) : (
        <p className="text-xs text-slate-500">Save the profile first to upload a website photo.</p>
      )}
    </div>
  );
}

/**
 * Child upload helper component
 */
function DocumentUploadField({ label, uploadType, role, formData }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const dispatch = useDispatch();

  const handleFileUpload = (file) => {
    if (file) {
      setIsModalOpen(false);
      dispatch(UploadFile(file, formData?._id, uploadType, role));
    }
  };

  const handleMultipleFileUploads = (file) => {
    if (file) {
      setIsModalOpen(false);
      dispatch(UploadFiles(file, formData?._id, uploadType, role));
    }
  };

  const handleImageUpload = async (file) => {
    if (file) {
      setIsModalOpen(false);
      const res = await dispatch(UploadImages(file, formData?._id, uploadType, role));
      if (res === true) toast.success('Image uploaded');
    }
  };

  const handleDelete = () => {
    if (!['pictures', 'otherDocs'].includes(uploadType)) {
      dispatch(RemoveFile(formData?.[uploadType], formData?._id, uploadType, role));
    } else {
      const fileIds =
        uploadType === 'otherDocs'
          ? formData?.[uploadType]?.map((i) => i?.id)
          : formData?.[uploadType];
      dispatch(DeleteImages({ fileIds }, formData?._id, role, uploadType));
    }
  };

  const hasFile = Boolean(
    formData?.[uploadType] &&
      (Array.isArray(formData[uploadType]) ? formData[uploadType].length > 0 : true)
  );

  return (
    <div className="p-4 bg-white rounded-xl border border-slate-200/90 shadow-2xs hover:border-slate-300 transition space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-slate-700 tracking-wide uppercase">
          {label}
        </span>
        {hasFile ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            Attached
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
            No document
          </span>
        )}
      </div>

      {uploadType === 'otherDocs' && Array.isArray(formData?.[uploadType]) && formData[uploadType].length > 0 && (
        <div className="max-h-24 overflow-y-auto space-y-1 bg-slate-50 p-2 rounded-lg border border-slate-200 text-xs">
          {formData[uploadType].map((f, idx) => (
            <a
              key={idx}
              href={`${END_POINT}/${role}/get/${f?.id}`}
              target="_blank"
              rel="noreferrer"
              className="text-sky-600 hover:underline block truncate font-medium"
            >
              {f?.name || `Document #${idx + 1}`}
            </a>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <Button
          variant="outline"
          size="xs"
          onClick={() => setIsModalOpen(uploadType)}
          icon={<FiUpload className="w-3 h-3" />}
        >
          Upload
        </Button>
        {hasFile && (
          <>
            <Button
              variant="outline"
              size="xs"
              onClick={() => {
                if (uploadType !== 'pictures') {
                  window.open(`${END_POINT}/${role}/get/${formData?.[uploadType]}`);
                } else {
                  setShowImage(true);
                }
              }}
              icon={<FiEye className="w-3 h-3" />}
            >
              View
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={handleDelete}
              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
              icon={<FiTrash2 className="w-3 h-3" />}
            >
              Delete
            </Button>
          </>
        )}
      </div>

      {isModalOpen !== 'pictures' ? (
        uploadType !== 'otherDocs' && (
          <FileUploadModal
            isOpen={Boolean(isModalOpen)}
            closeModal={() => setIsModalOpen(false)}
            onFileUpload={handleFileUpload}
            uploadType={uploadType}
          />
        )
      ) : (
        <ImageUploadModal
          isOpen={Boolean(isModalOpen)}
          closeModal={() => setIsModalOpen(false)}
          onImageUpload={handleImageUpload}
        />
      )}

      {showImage && (
        <ImageShowModal
          isOpen={showImage}
          closeModal={() => setShowImage(false)}
          Images={formData?.[uploadType]}
        />
      )}

      {isModalOpen === 'otherDocs' && (
        <MultipleFileUploads
          isOpen={Boolean(isModalOpen)}
          closeModal={() => setIsModalOpen(false)}
          onFileUpload={handleMultipleFileUploads}
          uploadType={uploadType}
        />
      )}
    </div>
  );
}

